import { ClaimTableSelectFilter } from 'pages/claims/components/ClaimTableSelectFilter';
import {
  ClaimStatusFilter,
  type ClaimStatusFilterProps,
} from 'pages/claims/components/ClaimStatusFilter';
import { getStatusSelectOptions } from 'pages/claims/components/ClaimToolBarComponents/claimTabFilterConfig';
import { useClaimTabFilterStore } from 'store/ClaimTabFilterStore';
import {
  getProviderBilledDate,
  getProviderRemittanceDate,
  getProviderOutstandingAmount,
} from 'pages/claims/utils/providerClaimColumns';
//React
import { useEffect, useState } from 'react';

// Refine
import { useTranslation, useNotification } from '@refinedev/core';

// Mantine
import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  Flex,
  Group,
  Menu,
  Pill,
  Text,
  Tooltip,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { MRT_ColumnDef } from 'mantine-react-table';

// Tabler
import {
  IconCheck,
  IconExternalLink,
  IconFilter,
  IconNotebook,
  IconInfoCircle,
  IconLock,
} from '@tabler/icons-react';

// Interfaces
import {
  Claim,
  Debit,
  EOB,
  Organization,
  Payor,
  PayorType,
  Provider,
  Remittance,
  Site,
  Tariff,
} from 'interfaces';

// Constants
import {
  healthcardSigningAuthorityOptions,
  provinceOptions,
} from 'constants/provinceConstants';
import { getSpecialtyCodes } from 'constants/specialtyCodes';
import {
  getServiceLocationIndicatorOptions,
  getClaimSelectOptions,
  getSKClaimFieldOptions,
  type SKClaimField,
} from 'constants/claimFieldOptions';

// ENUM
import {
  formatPatientName,
  hospitals,
  hospitalOptions,
  claimStatusEnumValues,
  CLAIM_TARIFF_STATUS,
  UNRECONCILED_CLAIM_TARIFF_STATUSES,
  getLocationOfServiceOptions,
  isBCPayor,
} from '@billing/validation';

// Store
import { useClaimDraftStore } from 'store/ClaimDraftStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

//Utils
import { CountReturnTypes } from 'api/utils/queries';
import { getStatusChipPresentation } from 'utils/badgeColors';
import {
  applyClaimTariffTotalsWithOverrides,
  resolveTariffRequestedAmount,
} from 'utils/tariffUtils';
import { getValueByPath } from 'utils/getValueByPath';
import { groupDebitEOBs } from 'utils/eobUtils';
import {
  determineActions,
  Action,
  ActionHandlerContext,
  canUpdateSubmittedClaimStatus,
  isSubmittedClaimPayorEditable,
} from 'utils/actionsUtils';
import {
  compareDateAsc,
  compareDateDesc,
  formatDateOnly,
  formatDateTime,
  formatTimeOnly,
  nowISO,
} from 'utils/dateUtils';

//Components
import {
  DataBadge,
  DiagnosisSearch,
  DisplayEOB,
  EOBDetailsModal,
  EOBCard,
  ProviderSearch,
  ICD9Badge,
  ModalBillingNumberInput,
  ModalDataText,
  ModalDateInput,
  ModalDateTimeInput,
  ModalSelectInput,
  ModalGroupedSelectInput,
  ModalTariffSelect,
  ModalTextArea,
  ModalTextInput,
  ModalProviderGroupSelect,
  ModalUploadLinkInput,
} from 'components/common';

import { ClaimAlerts } from 'pages/claims/components/ClaimAlerts';
import { LinkedUploadFilterSelect } from 'pages/claims/components/LinkedUploadFilterSelect';
import {
  CLAIM_ACTION_COLUMN_ID,
  ClaimActionsMenu,
  ClaimTariffActionsMenu,
  useActiveForm,
} from 'pages/claims/components';

// Hooks
import { useCanCreate, useCanModify, useCanView } from 'hooks';
import {
  getUserFullName,
  getUserNameWithTitlePrefix,
} from 'hooks/useGetUsersDisplayInfo';

const joinSearchableParts = (...parts: Array<string | null | undefined>) =>
  parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(' ');

const getPatientSearchValue = (row: Claim) =>
  joinSearchableParts(
    row.patient
      ? formatPatientName(row.patient.firstName, row.patient.lastName)
      : '',
    row.patient?.dateOfBirth ? formatDateOnly(row.patient.dateOfBirth) : '',
    row.patient?.dateOfBirth || ''
  );

const getPhinSearchValue = (row: Claim) =>
  joinSearchableParts(row.healthcard?.number, row.healthcard?.registration);

const getProviderSearchValue = (row: Claim) =>
  joinSearchableParts(
    row.provider ? getUserNameWithTitlePrefix(row.provider) : '',
    row.provider?.specialty?.description,
    row.provider?.specialtyCode
  );

const renderClaimLevelPlaceholder = (mutedColor: string) => (
  <Text size="sm" c={mutedColor}>
    —
  </Text>
);

const getClaimEditAvailability = (claim: Claim, payors: Payor[]) => {
  const isSubmitted = claimHasTariffStatus(
    claim,
    CLAIM_TARIFF_STATUS.SUBMITTED
  );
  const isQueried = claimHasTariffStatus(claim, CLAIM_TARIFF_STATUS.QUERIED);
  const submittedPayorType =
    claim.payor?.type ??
    payors.find((payor) => payor.id === claim.payorID)?.type;
  const isEditable =
    (!isSubmitted ||
      isSubmittedClaimPayorEditable(submittedPayorType) ||
      canUpdateSubmittedClaimStatus(claim)) &&
    !isQueried;

  return { isSubmitted, isQueried, isEditable };
};

interface SKClaimFieldSelectProps {
  field: SKClaimField;
  labelKey: string;
  defaultLabel: string;
  dataTestId: string;
  currentValue: string | null | undefined;
  rowPayor: Payor | null | undefined;
  disabled: boolean;
}

const SKClaimFieldSelect = ({
  field,
  labelKey,
  defaultLabel,
  dataTestId,
  currentValue,
  rowPayor,
  disabled,
}: SKClaimFieldSelectProps) => {
  const { translate } = useTranslation();
  const { form, prefix } = useActiveForm();
  const payorPath = prefix ? `${prefix}.payor` : 'payor';
  const formPayor = getValueByPath(form.getValues(), payorPath) as
    | Payor
    | null
    | undefined;
  const payor = formPayor === undefined ? rowPayor : formPayor;
  const options = getSKClaimFieldOptions(field, payor?.province, translate);

  if (
    currentValue?.trim() &&
    !options.some(({ value }) => value === currentValue)
  ) {
    options.push({
      label: `${currentValue} (INVALID)`,
      value: currentValue,
      disabled: true,
    });
  }

  return (
    <ModalSelectInput
      label={translate(labelKey, defaultLabel)}
      accessorKey={field}
      dataTestId={dataTestId}
      options={options}
      required={false}
      disabled={disabled}
    />
  );
};

// Firebase
import { isFeatureEnabled } from 'firebase';
import {
  buildClaimRowId,
  getRowDisplayStatus,
  getTariffStatus,
  isClaimParentRow,
  isClaimTariffRow,
  getParentClaim,
  getLatestNonRetractionDisplayedDebit,
  type ClaimTableRow,
} from 'pages/claims/utils/claimTableRows';
import {
  defineClaimTariffFilter,
  getLatestDebitEOBCodeFilterValue,
  getLatestDebitEOBDescriptionFilterValue,
  getLatestDebitDifferenceAmountFilterValue,
  getLatestDebitMicrofilmFilterValue,
  getLatestDebitPaidAmountFilterValue,
  matchAllNumericFilters,
  matchAllTextFilters,
  matchAnyExactFilter,
  matchLatestEOBCodeFilters,
  matchPaidAmountFilters,
} from 'pages/claims/utils/claimTariffRowFilters';
import {
  isReconcileEligibleTariffStatus,
  isResubmittableTariffStatus,
  isWriteOffEligibleTariffStatus,
  claimHasAnyTariffStatus,
  claimHasTariffStatus,
} from 'utils/claimStatusUtils';
import { getProviderStatusPresentation } from 'utils/providerClaimStatus';

const formatCurrency = (value: number) =>
  Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const getTariffSearchValue = (row: ClaimTableRow): string =>
  (Array.isArray(row.tariffs) ? row.tariffs : [])
    .flatMap((tariff) => [
      tariff.code,
      tariff.description,
      `${String(tariff.prefix ?? '')}${String(tariff.code ?? '')}`,
    ])
    .filter((value): value is string => Boolean(value))
    .join(' ');

const getTariffFieldValues = <TValue,>(
  row: ClaimTableRow,
  getValue: (tariff: ClaimTableRow['tariffs'][number]) => TValue
): TValue[] => (Array.isArray(row.tariffs) ? row.tariffs : []).map(getValue);

type RequestedAmountTariff = {
  id: string;
  status?: string | null;
  total?: number | string | null;
};

const getComputedRequestedTariffs = (claim: Claim) =>
  applyClaimTariffTotalsWithOverrides(claim);

const getClaimTariffRequestedAmount = (
  tariff: RequestedAmountTariff,
  computedTariffs: RequestedAmountTariff[],
  claim: Claim,
  debitSourceClaim: Claim = claim
) => {
  const persistedTariffStatus = tariff.status?.trim();
  const tariffStatus = getTariffStatus(claim, tariff as Tariff);
  const latestDebit = getLatestNonRetractionDisplayedDebit(
    debitSourceClaim,
    tariff.id
  );
  const isPaidTariff = [persistedTariffStatus, tariffStatus].some(
    (status) => status?.toLowerCase() === CLAIM_TARIFF_STATUS.PAID.toLowerCase()
  );
  if (isPaidTariff && latestDebit?.requestedAmount != null) {
    return Number(latestDebit.requestedAmount);
  }

  const persistedTotal = Number(tariff.total ?? 0);
  if (persistedTotal) return persistedTotal;

  const computedTariff = computedTariffs.find(
    (computed) => computed.id === tariff.id
  );

  return Number(computedTariff?.total ?? 0);
};

export const getClaimRequestedAmount = (row: Claim) => {
  const claim = getParentClaim(row);
  const tariffs = claim.tariffs ?? [];

  if (isClaimTariffRow(row)) {
    const tariff = row.tariffs?.[0];
    if (!tariff) return 0;
    const tariffStatus =
      tariff.status?.trim() || getTariffStatus(claim, tariff);
    const latestDebit = getLatestNonRetractionDisplayedDebit(row, tariff.id);
    if (
      tariffStatus.toLowerCase() === CLAIM_TARIFF_STATUS.PAID.toLowerCase() &&
      latestDebit?.requestedAmount != null
    ) {
      return Number(latestDebit.requestedAmount);
    }

    if (Number(tariff.total ?? 0)) return Number(tariff.total);

    return getClaimTariffRequestedAmount(
      tariff,
      getComputedRequestedTariffs(claim),
      claim,
      row
    );
  }

  if ('subRows' in row && Array.isArray(row.subRows) && row.subRows.length) {
    return row.subRows.reduce(
      (total, subRow) => total + getClaimRequestedAmount(subRow),
      0
    );
  }

  const computedTariffs = tariffs.some((tariff) => !Number(tariff.total ?? 0))
    ? getComputedRequestedTariffs(claim)
    : [];

  return tariffs.reduce(
    (total, tariff) =>
      total + getClaimTariffRequestedAmount(tariff, computedTariffs, claim),
    0
  );
};

const getDebitCreatedAtMillis = (debit: Debit) => {
  const millis = debit.createdAt ? new Date(debit.createdAt).getTime() : 0;
  return Number.isNaN(millis) ? 0 : millis;
};

const getLatestNonRetractionDebitByTariffID = (debits: Debit[] = []) => {
  const byTariffID = new Map<string, Debit>();

  debits.forEach((debit) => {
    if (debit.type === 'RETRACTION') return;

    const tariffID = String(debit.tariffID);
    const current = byTariffID.get(tariffID);
    if (
      !current ||
      getDebitCreatedAtMillis(debit) > getDebitCreatedAtMillis(current)
    ) {
      byTariffID.set(tariffID, debit);
    }
  });

  return byTariffID;
};

const getClaimDifferenceAmount = (row: Claim) => {
  const claim = getParentClaim(row);
  const tariffs = isClaimTariffRow(row)
    ? row.tariffs ?? []
    : claim.tariffs ?? [];
  const debits = isClaimTariffRow(row) ? row.debits ?? [] : claim.debits ?? [];
  const latestDebitByTariffID = getLatestNonRetractionDebitByTariffID(debits);
  const normalizedStatus = claim.status as string | undefined;

  return tariffs.reduce((total, tariff) => {
    const debit = latestDebitByTariffID.get(tariff.id);
    const paidValue = Number(debit?.paidAmount ?? 0);
    const requestedAmountStatus =
      normalizedStatus === CLAIM_TARIFF_STATUS.PAID
        ? normalizedStatus
        : tariff.status ?? normalizedStatus;
    const submittedAmount = resolveTariffRequestedAmount(
      tariff as Tariff,
      requestedAmountStatus,
      debit
    );

    return total + Number((paidValue - submittedAmount).toFixed(2));
  }, 0);
};

const getClaimPaidAmount = (row: Claim): number | undefined => {
  const payorDebits = (row.debits ?? []).filter(
    (debit) => debit.payorID === row.payorID
  );

  if (!payorDebits.length) return undefined;

  return payorDebits.reduce(
    (total, debit) => total + Number(debit.paidAmount ?? 0),
    0
  );
};

const EOBCodesCell: React.FC<{
  eobs: DisplayEOB[];
  borderColor: string;
  mutedColor: string;
}> = ({ eobs, borderColor, mutedColor }) => {
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('dark');
  const [selectedEOB, setSelectedEOB] = useState<DisplayEOB | null>(null);

  if (!eobs.length) {
    return (
      <Text size="xs" c={mutedColor}>
        --
      </Text>
    );
  }

  return (
    <>
      <Flex wrap="wrap" gap={4} align="flex-start">
        {eobs.map((eob) => (
          <Tooltip
            key={eob.id}
            label={`Click to view more details for EOB ${eob.code}`}
            position="top"
            withArrow
          >
            <Badge
              component="button"
              type="button"
              size="xs"
              variant="light"
              radius="sm"
              color="gray"
              data-testid={`claim-eob-badge-${eob.code}`}
              aria-label={`View details for EOB ${eob.code}`}
              onClick={() => setSelectedEOB(eob)}
              styles={{
                root: {
                  cursor: 'pointer',
                  backgroundColor:
                    computedColorScheme === 'dark'
                      ? theme.colors.dark[4]
                      : theme.colors.gray[1],
                  color:
                    computedColorScheme === 'dark'
                      ? theme.colors.gray[1]
                      : theme.colors.gray[7],
                  border: `1px solid ${borderColor}`,
                  transition: 'transform 120ms ease, box-shadow 120ms ease',
                },
              }}
            >
              {`${eob.code}`}
            </Badge>
          </Tooltip>
        ))}
      </Flex>
      <EOBDetailsModal
        opened={Boolean(selectedEOB)}
        eob={selectedEOB}
        onClose={() => setSelectedEOB(null)}
      />
    </>
  );
};

interface ClaimColumnsProps {
  providerProp: Provider[];
  siteProp: Site[];
  payorsProp: Payor[];
  remittancesProp: Remittance[];
  eobsProp: EOB[];
  eobCountsProp: CountReturnTypes;
  confidenceByClaimID?: Record<string, number | null>;
  onEobFilterOpen?: () => void;
  claimStatuses: CountReturnTypes;
  invalidData: string[];
  actionColumnRows?: Claim[];
  setSelectedClaim: (claim: Claim) => void;
  onOpenClaimEdit?: (claim: Claim) => void;
  setHistoryOpen: (open: boolean) => void;
  setDebitOpen: (open: boolean) => void;
  setSubmissionOpen: (open: boolean) => void;
  setViewBatchOpen: (open: boolean) => void;
  setInvoiceOpen: (open: boolean) => void;
  setCancelClaimOpen: (open: boolean) => void;
  setQueryModalOpen: (open: boolean) => void;
  setInvoiceModalOpen: (open: boolean) => void;
  setResubmitOpen: (open: boolean) => void;
  setReconcileOpen: (open: boolean) => void;
  setMatchOpen: (open: boolean) => void;
  setWriteOffOpen: (open: boolean) => void;
  setMarkReadyOpen: (open: boolean) => void;
  setMarkIncompleteOpen: (open: boolean) => void;
  setMarkHistoricalOpen: (open: boolean) => void;
  setMarkUninsuredOpen: (open: boolean) => void;
  setMarkPendingWriteOffOpen: (open: boolean) => void;
  setMarkPaidOpen: (open: boolean) => void;
  setInternalCommentOpen: (open: boolean) => void;
  setReceiptOpen: (open: boolean) => void;
  setActionRequestOpen: (open: boolean) => void;
  setViewActionsOpen: (open: boolean) => void;
  setSourceDataOpen?: (open: boolean) => void;
  setAttachmentsOpen?: (open: boolean) => void;
  setSupportingDocumentOpen?: (open: boolean) => void;
  onDuplicateClaim?: (claim: Claim) => void;
  onClaimReconcile?: (claim: Claim) => void;
  onTariffResubmit?: (payload: { claimID: string; tariffID: string }) => void;
  onTariffReconcile?: (payload: { claimID: string; tariffID: string }) => void;
  onTariffWriteOff?: (payload: { claimID: string; tariffID: string }) => void;
  onTariffMarkAs?: (payload: {
    claimID: string;
    tariffID: string;
    status: string;
  }) => void;
  dirtyFields: React.MutableRefObject<{ [key: string]: boolean }>;
  stagedTariffActionsRef?: React.MutableRefObject<{
    claimID: string | null;
    hasStagedActions: boolean;
  }>;
  isAdmin?: boolean;
  isProvider?: boolean;
  isAllSites?: boolean;
}

export const getLocationOfServiceForHospitalCode = (
  hospitalCode?: string | null
): string | null => {
  const code = hospitalCode?.trim();
  if (!code) return null;

  return hospitals.find((hospital) => hospital.code === code)?.region ?? null;
};

export const syncHospitalSelectionToClaimLocation = ({
  hospitalCode,
  hospitalNamePath,
  locationOfServicePath,
  payor,
  setFieldValue,
}: {
  hospitalCode: string | null;
  hospitalNamePath: string;
  locationOfServicePath: string;
  payor?: Pick<Payor, 'province'> | null;
  setFieldValue: (path: string, value: string | null) => void;
}) => {
  setFieldValue(hospitalNamePath, hospitalCode);

  if (isBCPayor(payor)) {
    return;
  }

  const locationOfService = getLocationOfServiceForHospitalCode(hospitalCode);
  if (locationOfService) {
    setFieldValue(locationOfServicePath, locationOfService);
  }
};

const ServiceStartDateRangeFilter = ({
  column,
  rangeFilterIndex,
}: {
  column: {
    getFilterValue: () => unknown;
    setFilterValue: (value: unknown) => void;
  };
  rangeFilterIndex?: number;
}) => {
  const index = rangeFilterIndex ?? 0;
  const filterValue = column.getFilterValue();
  const rangeValue = Array.isArray(filterValue)
    ? (filterValue[index] as string | Date | null | undefined)
    : null;

  const updateRangeValue = (value: string | null) => {
    const nextRange = Array.isArray(filterValue)
      ? [...filterValue]
      : [undefined, undefined];

    nextRange[index] = value || undefined;

    if (!nextRange[0] && !nextRange[1]) {
      column.setFilterValue(undefined);
      return;
    }

    column.setFilterValue(nextRange);
  };

  return (
    <DateInput
      aria-label={index === 0 ? 'Min' : 'Max'}
      allowDeselect
      clearable
      maxDate={nowISO()}
      onChange={updateRangeValue}
      onClick={(event) => event.stopPropagation()}
      placeholder={index === 0 ? 'Min' : 'Max'}
      styles={{ input: { minWidth: '8.5rem' } }}
      popoverProps={{ withinPortal: true }}
      value={rangeValue || null}
      valueFormat="YYYY-MM-DD"
      variant="unstyled"
    />
  );
};

export const ClaimColumns = <TRow extends Claim = Claim>({
  payorsProp,
  remittancesProp,
  siteProp,
  eobsProp,
  confidenceByClaimID = {},
  onEobFilterOpen,
  invalidData,
  actionColumnRows = [],
  setSelectedClaim,
  onOpenClaimEdit,
  setHistoryOpen,
  setDebitOpen,
  setSubmissionOpen,
  setViewBatchOpen,
  setInvoiceOpen,
  setCancelClaimOpen,
  setQueryModalOpen,
  setInvoiceModalOpen,
  setResubmitOpen,
  setReconcileOpen,
  setMatchOpen,
  setWriteOffOpen,
  setMarkReadyOpen,
  setMarkPaidOpen,
  setMarkIncompleteOpen,
  setMarkHistoricalOpen,
  setMarkUninsuredOpen,
  setMarkPendingWriteOffOpen,
  setReceiptOpen,
  setInternalCommentOpen,
  setActionRequestOpen,
  setViewActionsOpen,
  setSourceDataOpen = () => {},
  setAttachmentsOpen = () => {},
  setSupportingDocumentOpen = () => {},
  onDuplicateClaim,
  onClaimReconcile,
  onTariffResubmit,
  onTariffReconcile,
  onTariffWriteOff,
  onTariffMarkAs,
  dirtyFields,
  stagedTariffActionsRef,
  isAdmin = false,
  isProvider = false,
  isAllSites = false,
}: ClaimColumnsProps): MRT_ColumnDef<TRow>[] => {
  const theme = useMantineTheme();
  const { canModify } = useCanModify('claims');
  const { canCreate: canCreateQuery } = useCanCreate('queries');
  const { canView: canViewQueries } = useCanView('queries');
  const editDisabled = !canModify;
  const { translate } = useTranslation();
  const isActionManagementEnabled = isFeatureEnabled('action_management');
  const computedColorScheme = useComputedColorScheme('dark');
  const {
    activeTab,
    selectedTariffStatuses,
    toggleSubStatus,
    clearSubStatuses,
  } = useClaimTabFilterStore();
  const statusSelectOptions = getStatusSelectOptions(
    activeTab,
    isProvider,
    translate
  );
  const statusFilterProps: ClaimStatusFilterProps = {
    data: statusSelectOptions,
    value:
      statusSelectOptions.find(
        ({ value, disabled }) =>
          !disabled && selectedTariffStatuses.includes(value)
      )?.value ?? null,
    onChange: (value) => {
      if (!value) {
        clearSubStatuses();
        return;
      }
      if (
        statusSelectOptions.some(
          (option) => option.value === value && !option.disabled
        ) &&
        !selectedTariffStatuses.includes(value)
      ) {
        toggleSubStatus(value);
      }
    },
  };
  const draftStore = useClaimDraftStore();
  const actionColumnWidth =
    84 +
    actionColumnRows.reduce((maxIndicatorCount, claim) => {
      const { isEditable } = getClaimEditAvailability(claim, payorsProp);
      const hasDraft =
        draftStore.getEditDraftById(buildClaimRowId(claim.id))?.id === claim.id;
      return Math.max(
        maxIndicatorCount,
        Number(!isEditable) + Number(hasDraft)
      );
    }, 0) *
      20;
  const { organization, timezone } = useCurrentSiteStore();
  const { id: currentUserID } = useCurrentUserStore();
  const { open } = useNotification();

  //TODO: add EOB counts to eob props
  const eobMap = new Map<string, EOB>();
  eobsProp.forEach((eob: EOB) => {
    if (eob && !eobMap.has(eob?.code)) {
      eobMap.set(eob.code, eob);
    }
  });
  const uniqueEobs = Array.from(eobMap.values());

  const getActivePayorType = (table: {
    getState: () => { columnFilters: Array<{ id: string; value: unknown }> };
  }): PayorType | undefined => {
    const value = table
      .getState()
      .columnFilters.find((filter) => filter.id === 'payor.type')?.value;
    return value === PayorType.PRIVATE ||
      value === PayorType.PUBLIC ||
      value === PayorType.DIRECT
      ? value
      : undefined;
  };

  const getActiveLinkedTaskAssignee = (table: {
    getState: () => { columnFilters: Array<{ id: string; value: unknown }> };
  }): string | undefined => {
    const value = table
      .getState()
      .columnFilters.find(
        (filter) => filter.id === 'intakeItem.assigneeUserID'
      )?.value;
    return typeof value === 'string' && value.trim() ? value : undefined;
  };

  const addInvalidOptions = (
    options: { label: string; value: string; disabled?: boolean }[],
    currentValue: string
  ) => {
    const values = options.map((option) => option.value);
    if (!values.includes(currentValue) && currentValue && currentValue !== '') {
      options.push({
        label: `${currentValue} (INVALID)`,
        value: currentValue,
        disabled: true,
      });
    }
  };

  const mutedColor =
    computedColorScheme === 'dark'
      ? theme.colors.gray[4]
      : theme.colors.gray[6];

  const getTariffCodeBadgePresentation = (status: string) =>
    isProvider
      ? getProviderStatusPresentation(status, computedColorScheme)
      : getStatusChipPresentation(status, computedColorScheme);

  const getClaimConfidenceScore = (claim: Claim) =>
    confidenceByClaimID[claim.id] ?? null;

  const renderStatusBadge = ({
    label,
    tooltip,
    provider,
  }: {
    label: string;
    tooltip?: string;
    provider?: boolean;
  }) => {
    const statusChip = provider
      ? getProviderStatusPresentation(label, computedColorScheme)
      : getStatusChipPresentation(label, computedColorScheme);

    const content = tooltip ? (
      <Group gap={4} wrap="nowrap">
        <span>{label}</span>
        <IconInfoCircle
          size={12}
          stroke={2}
          role="img"
          aria-label={translate(
            'pages:claims.statusInformation',
            'Status information'
          )}
        />
      </Group>
    ) : (
      label
    );

    const badge = (
      <Badge
        variant="light"
        size="sm"
        radius="xl"
        className="claims-status-badge"
        style={{
          backgroundColor: statusChip.backgroundColor,
          border: `1px solid ${statusChip.borderColor}`,
          color: statusChip.textColor,
        }}
      >
        {content}
      </Badge>
    );

    if (!tooltip) return badge;

    return (
      <Tooltip label={tooltip} withArrow multiline w={220} position="top">
        {badge}
      </Tooltip>
    );
  };

  const remittanceDateColumn: MRT_ColumnDef<TRow> = {
    accessorKey: 'debits.createdAt',
    header: translate(
      'pages:claims.table.columns.remittanceDate',
      'Remittance Date'
    ),
    accessorFn: getProviderRemittanceDate,
    enableEditing: false,
    Cell: ({ cell, row }) => (
      <Text size="sm">
        {cell.getValue<string>()
          ? formatDateOnly(cell.getValue<string>(), {
              timezone: row.original.site.timezone,
            })
          : ''}
      </Text>
    ),
  };

  const providerPrototypeColumns: MRT_ColumnDef<TRow>[] = isProvider
    ? [
        {
          accessorKey: 'tariffs.lastSubmittedAt',
          header: translate(
            'pages:claims.table.columns.billedDate',
            'Billed Date'
          ),
          accessorFn: getProviderBilledDate,
          enableEditing: false,
          Cell: ({ cell, row }) => (
            <Text size="sm">
              {cell.getValue<string>()
                ? formatDateOnly(cell.getValue<string>(), {
                    timezone: row.original.site.timezone,
                  })
                : ''}
            </Text>
          ),
        },
        remittanceDateColumn,
        {
          accessorKey: 'outstandingAmount',
          header: translate(
            'pages:claims.table.columns.outstanding',
            'Outstanding'
          ),
          accessorFn: getProviderOutstandingAmount,
          enableEditing: false,
          Cell: ({ cell }) => (
            <Text size="sm" fw={600}>
              {formatCurrency(cell.getValue<number>())}
            </Text>
          ),
        },
      ]
    : [];

  const tariffCommentColumns: MRT_ColumnDef<TRow>[] = [
    {
      accessorKey: 'tariffs.latestComment',
      header: translate(
        'pages:claims.table.columns.tariffComment.header',
        'Biller Comment'
      ),
      enableEditing: false,
      size: 280,
      accessorFn: (row) => {
        if (isClaimTariffRow(row)) {
          return row.tariffs[0]?.latestComment?.trim() || '';
        }
        const tariffs = isClaimParentRow(row)
          ? row.subRows.flatMap((child) => child.tariffs)
          : row.tariffs ?? [];
        const count = tariffs.filter((tariff) =>
          tariff.latestComment?.trim()
        ).length;
        if (!count) return '';
        return translate(
          'pages:claims.table.columns.tariffComment.summary',
          { count },
          count === 1
            ? '1 comment · expand to view'
            : `${count} comments · expand to view`
        );
      },
      Cell: ({ cell, row }) => (
        <Text
          size="sm"
          c={isClaimTariffRow(row.original) ? undefined : 'dimmed'}
          style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {cell.getValue<string>()}
        </Text>
      ),
    },
  ];

  const serviceTimeColumn: MRT_ColumnDef<TRow>[] = isProvider
    ? [
        {
          accessorKey: 'serviceStart',
          header: 'Service Date',
          accessorFn: (row) =>
            formatDateOnly(row.serviceStart, { timezone: row.site.timezone }),
          Cell: ({ cell, row }) =>
            isClaimTariffRow(row.original) ? (
              renderClaimLevelPlaceholder(mutedColor)
            ) : (
              <Text style={{ textAlign: 'center' }}>
                {cell.getValue() as string}
              </Text>
            ),
          Edit: () => {
            return (
              <ModalDateTimeInput
                label="Service Start"
                accessorKey="serviceStart"
                hotkey="alt+d"
                required={true}
                disabled={editDisabled}
              />
            );
          },
          sortingFn: (rowA, rowB) =>
            compareDateAsc(
              rowA.original.serviceStart,
              rowB.original.serviceStart
            ),
          mantineFilterDateInputProps: {
            valueFormat: 'YYYY-MM-DD',
            clearable: true,
            max: nowISO(),
          },
          Filter: ServiceStartDateRangeFilter,
          filterVariant: 'date-range',
        },
        {
          header: 'Service Time',
          accessorKey: 'serviceTime',
          accessorFn: (row) =>
            formatTimeOnly(row.serviceStart, {
              timezone: row.site.timezone,
            }),
          Cell: ({ cell }) => {
            const claim = cell.row.original;
            return (
              <Text style={{ textAlign: 'center' }}>
                {formatTimeOnly(claim.serviceStart, {
                  timezone: claim.site.timezone,
                })}
              </Text>
            );
          },
          sortingFn: (rowA, rowB) =>
            compareDateAsc(
              rowA.original.serviceStart,
              rowB.original.serviceStart
            ),
          visibleInShowHideMenu: true,
        },
      ]
    : [
        {
          accessorKey: 'serviceStart',
          header: 'Service Start',
          accessorFn: (row) =>
            formatDateTime(row.serviceStart, {
              timezone: row.site?.timezone,
            }),
          Cell: ({ cell, row }) =>
            isClaimTariffRow(row.original) ? (
              renderClaimLevelPlaceholder(mutedColor)
            ) : (
              <Text style={{ textAlign: 'center' }}>
                {cell.getValue() as string}
              </Text>
            ),
          Edit: () => {
            return (
              <ModalDateTimeInput
                label="Service Start"
                accessorKey="serviceStart"
                hotkey="alt+d"
                required={true}
                disabled={editDisabled}
              />
            );
          },
          sortingFn: (rowA, rowB) =>
            compareDateAsc(
              rowA.original.serviceStart,
              rowB.original.serviceStart
            ),
          mantineFilterDateInputProps: {
            valueFormat: 'YYYY-MM-DD',
            clearable: true,
            max: nowISO(),
          },
          Filter: ServiceStartDateRangeFilter,
          filterVariant: 'date-range',
        },
      ];
  //TODO: add eob column
  const columns: MRT_ColumnDef<TRow>[] = [
    /************** Claim Fields ***********************/
    {
      id: CLAIM_ACTION_COLUMN_ID,
      columnDefType: 'display',
      header: '',
      size: actionColumnWidth,
      minSize: actionColumnWidth,
      maxSize: actionColumnWidth,
      grow: false,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enablePinning: true,
      Cell: ({ row }) => {
        if (isClaimTariffRow(row.original)) {
          const rowOriginal = row.original;
          const tariff: Tariff | undefined = rowOriginal.tariffs?.[0] as Tariff;
          if (!tariff) return null;
          const tariffStatus = getTariffStatus(rowOriginal, tariff);
          const canReconcileTariff =
            isReconcileEligibleTariffStatus(tariffStatus);
          const canResubmitTariff = isResubmittableTariffStatus(tariffStatus);
          const canWriteOffTariff =
            isWriteOffEligibleTariffStatus(tariffStatus);
          const canMatchTariff = tariffStatus === CLAIM_TARIFF_STATUS.UNMATCHED;

          return (
            <Flex justify="flex-start" align="center" w="100%">
              <Text size="xs" style={{ display: 'none' }}>
                {tariffStatus}
              </Text>
              <ClaimTariffActionsMenu
                claimID={rowOriginal.parentClaimId}
                tariff={tariff}
                canModify={canModify}
                canReconcileTariff={canReconcileTariff}
                canResubmitTariff={canResubmitTariff}
                canWriteOffTariff={canWriteOffTariff}
                canMatchTariff={canMatchTariff}
                onTariffResubmit={onTariffResubmit}
                onTariffReconcile={onTariffReconcile}
                onTariffWriteOff={onTariffWriteOff}
                onTariffMatch={() => {
                  setSelectedClaim(rowOriginal.parentClaim);
                  setMatchOpen(true);
                }}
                onTariffMarkAs={onTariffMarkAs}
              />
            </Flex>
          );
        }

        const draftID = draftStore.getEditDraftById(row.id)?.id;
        const isDraft = row.original.id === draftID;
        const isRowInvalid = invalidData?.includes(row.original.id);
        const actions = determineActions(
          row.original,
          isProvider,
          isAdmin,
          translate,
          canViewQueries
        );
        const { isSubmitted, isQueried, isEditable } = getClaimEditAvailability(
          row.original,
          payorsProp
        );
        const claimConfidenceScore = getClaimConfidenceScore(row.original);
        const needsManualReview = claimConfidenceScore === 0;
        const commonContext: ActionHandlerContext = {
          claim: row.original,
          canModify,
          canCreateQuery,
          setSelectedClaim,
          setHistoryOpen,
          setDebitOpen,
          setSubmissionOpen,
          setViewBatchOpen,
          setInvoiceOpen,
          setCancelClaimOpen,
          setQueryModalOpen,
          setInvoiceModalOpen,
          setResubmitOpen,
          setReconcileOpen,
          setMatchOpen,
          setWriteOffOpen,
          setMarkReadyOpen,
          setMarkPaidOpen,
          setMarkIncompleteOpen,
          setMarkHistoricalOpen,
          setMarkUninsuredOpen,
          setMarkPendingWriteOffOpen,
          setInternalCommentOpen,
          setReceiptOpen,
          setActionRequestOpen,
          setViewActionsOpen,
          setSourceDataOpen,
          setAttachmentsOpen,
          setSupportingDocumentOpen,
          onDuplicateClaim,
          onClaimReconcile,
          onUnhandledAction: (
            unhandledAction: Action,
            context: ActionHandlerContext
          ) => {
            // Ensure the claim remains selected even if no handler is provided.
            context.setSelectedClaim?.(context.claim);
            open({
              message: 'Not Yet Implemented',
              description: `No handler defined for action: ${unhandledAction}`,
              type: 'error',
            });
          },
        };

        return (
          <Flex
            justify="flex-start"
            align="center"
            w="100%"
            wrap="nowrap"
            gap={2}
          >
            <ClaimActionsMenu
              claim={row.original}
              actions={actions}
              actionContext={commonContext}
              isProvider={isProvider}
              canModify={canModify}
            />
            {!isEditable && (
              <Tooltip
                label={
                  // Queried claims are always locked; submitted claims are
                  // only locked when their payor type does not allow updates.
                  isSubmitted
                    ? translate(
                        'actionDisabledReason.editingDisabledClaimSubmitted'
                      )
                    : translate(
                        'actionDisabledReason.editingDisabledBeingQueried'
                      )
                }
                withArrow
                position="top"
              >
                <IconLock
                  size={18}
                  aria-label={translate(
                    'pages:claims.alerts.submittedClaim',
                    'Submitted claim'
                  )}
                  data-testid="submitted-claim"
                />
              </Tooltip>
            )}
            {isDraft && (
              <Tooltip
                label={translate(
                  'pages:claims.alerts.draftSaved',
                  'Draft Saved'
                )}
                withArrow
                position="top"
              >
                <IconNotebook
                  size={18}
                  aria-label={translate(
                    'pages:claims.alerts.draftClaim',
                    'Draft claim'
                  )}
                />
              </Tooltip>
            )}
            <ClaimAlerts
              claimID={row.original.id}
              invalid={Boolean(isRowInvalid)}
              manualReview={needsManualReview}
              queryResponse={Boolean(
                row.original.hasQueryResponse &&
                  isQueried &&
                  (!('rowType' in row.original) ||
                    row.original.rowType === 'claim')
              )}
              canViewQueries={canViewQueries}
              canEdit={Boolean(
                onOpenClaimEdit &&
                  canModify &&
                  !isProvider &&
                  !isAdmin &&
                  isEditable
              )}
              actionManagementEnabled={isActionManagementEnabled}
              actionRequests={row.original.actionRequests ?? []}
              isProvider={isProvider}
              onOpenActions={() => {
                setSelectedClaim(row.original);
                setViewActionsOpen(true);
              }}
              onEdit={() => onOpenClaimEdit?.(row.original)}
            />
          </Flex>
        );
      },
      mantineTableHeadCellProps: {
        style: { textAlign: 'left', paddingInline: 2 },
      },
      mantineTableBodyCellProps: { style: { paddingInline: 2 } },
    },
    ...serviceTimeColumn,
    {
      accessorKey: 'serviceEnd',
      header: 'Service End',
      accessorFn: (row) =>
        isProvider
          ? formatDateOnly(row.serviceEnd)
          : formatDateTime(row.serviceEnd, {
              timezone: row.site?.timezone,
            }),
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text style={{ textAlign: 'center' }}>
            {cell.getValue() as string}
          </Text>
        ),
      Edit: () => {
        return (
          <ModalDateTimeInput
            label="Service End"
            accessorKey="serviceEnd"
            required={true}
            disabled={editDisabled}
          />
        );
      },
    },
    {
      accessorKey: 'version',
      header: 'Version',
      accessorFn: (row) => {
        return row.version;
      },
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <DataBadge>{cell.getValue()}</DataBadge>
        ),
      Edit: ({ cell }) => {
        const selectedValue =
          (cell.getValue() as number | string) === ''
            ? 1
            : (cell.getValue() as number | string);
        return (
          <ModalDataText
            value={selectedValue}
            title="Version"
            accessorKey="version"
            disabled={editDisabled}
          />
        );
      },
      maxSize: 50,
    },
    {
      accessorKey: 'claimNumber',
      header: 'Claim Number',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : cell.getValue() ? (
          <DataBadge>{cell.getValue()}</DataBadge>
        ) : (
          ''
        ),
      Edit: ({ cell }) => {
        const [selectedValue] = useState<string>(cell.getValue() as string);
        return (
          <ModalDataText
            value={selectedValue}
            title="Claim Number"
            accessorKey="claimNumber"
            disabled={editDisabled}
          />
        );
      },
      filterVariant: 'text',
      mantineFilterTextInputProps: {
        placeholder: 'Search Claim Number',
      },
    },
    {
      accessorKey: 'intakeItemID',
      header: translate(
        'pages:claims.table.columns.linkedUpload.header',
        'Linked Upload'
      ),
      Header: ({ table }) => {
        const activeAssigneeFilter = getActiveLinkedTaskAssignee(table);
        const isAssignedToMeActive =
          Boolean(currentUserID) && activeAssigneeFilter === currentUserID;

        return (
          <Group gap={6} wrap="nowrap">
            <span>
              {translate(
                'pages:claims.table.columns.linkedUpload.header',
                'Linked Upload'
              )}
            </span>
            {isAssignedToMeActive ? (
              <Tooltip
                label={translate(
                  'pages:claims.table.columns.linkedUpload.activeAssignedToMe',
                  'Assigned upload filter: My uploads'
                )}
              >
                <ActionIcon
                  aria-label={translate(
                    'pages:claims.table.columns.linkedUpload.openFilters',
                    'Open linked upload filters'
                  )}
                  className="mrt-table-head-cell-filter-label-icon"
                  size={18}
                  variant="transparent"
                  data-active
                  onClick={(event) => {
                    event.stopPropagation();
                    table.setShowColumnFilters(true);
                    setTimeout(() => {
                      const input =
                        table.refs.filterInputRefs.current['intakeItemID-0'];
                      input?.focus();
                    }, 100);
                  }}
                  style={
                    {
                      '--ai-bg': 'transparent',
                      '--ai-hover':
                        computedColorScheme === 'dark'
                          ? 'var(--mantine-color-dark-6)'
                          : 'var(--mantine-color-gray-1)',
                      '--ai-color': 'var(--mantine-color-white)',
                      '--ai-bd':
                        'calc(0.0625rem * var(--mantine-scale)) solid transparent',
                      '--ai-hover-color': 'var(--mantine-color-bright)',
                      color: 'var(--mantine-primary-color-filled)',
                    } as React.CSSProperties
                  }
                >
                  <IconFilter size="100%" />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Group>
        );
      },
      renderColumnActionsMenuItems: ({ internalColumnMenuItems, table }) => {
        const activeAssigneeFilter = getActiveLinkedTaskAssignee(table);
        const isAssignedToMeActive =
          Boolean(currentUserID) && activeAssigneeFilter === currentUserID;

        const applyAssignedToMeFilter = () => {
          if (!currentUserID) return;
          const currentFilters = table
            .getState()
            .columnFilters.filter(
              (filter) => filter.id !== 'intakeItem.assigneeUserID'
            );
          table.setColumnFilters([
            ...currentFilters,
            { id: 'intakeItem.assigneeUserID', value: currentUserID },
          ]);
        };

        const clearAssignedToMeFilter = () => {
          const currentFilters = table
            .getState()
            .columnFilters.filter(
              (filter) => filter.id !== 'intakeItem.assigneeUserID'
            );
          table.setColumnFilters(currentFilters);
        };

        return (
          <>
            <Menu.Item
              leftSection={
                isAssignedToMeActive ? <IconCheck size={16} /> : null
              }
              fw={isAssignedToMeActive ? 700 : undefined}
              onClick={applyAssignedToMeFilter}
              disabled={!currentUserID}
            >
              {translate(
                'pages:claims.table.columns.linkedUpload.filterByMyAssignedUploads',
                'Filter by My Assigned Uploads'
              )}
            </Menu.Item>
            <Menu.Item
              leftSection={
                !activeAssigneeFilter ? <IconCheck size={16} /> : null
              }
              fw={!activeAssigneeFilter ? 700 : undefined}
              onClick={clearAssignedToMeFilter}
            >
              {translate(
                'pages:claims.table.columns.linkedUpload.clearAssignedUploadFilter',
                'Clear Assigned Upload Filter'
              )}
            </Menu.Item>
            <Divider />
            {internalColumnMenuItems}
          </>
        );
      },
      accessorFn: (row) => {
        const intakeItem = row.intakeItem;
        if (!intakeItem) return '';
        return translate(
          'pages:claims.table.columns.linkedUpload.uploadNumberLabel',
          {
            intakeNumber: intakeItem.intakeNumber,
          }
        );
      },
      Cell: ({ cell, row }) => {
        if (isClaimTariffRow(row.original)) {
          return renderClaimLevelPlaceholder(mutedColor);
        }
        const value = (cell.getValue() as string) || '';
        if (!value) return null;
        const uploadID = cell.row.original.intakeItemID;
        if (!uploadID) return null;
        const openLinkedUpload = () => {
          if (typeof window === 'undefined') return;
          window.open(
            `/uploads/edit/${uploadID}`,
            '_blank',
            'noopener,noreferrer'
          );
        };

        return (
          <Group gap={4} wrap="nowrap">
            <span>
              <DataBadge>{value}</DataBadge>
            </span>
            <Tooltip
              label={translate(
                'pages:claims.table.columns.linkedUpload.openInNewTab',
                'Open linked upload in new tab'
              )}
              withArrow
            >
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  openLinkedUpload();
                }}
                aria-label={translate(
                  'pages:claims.table.columns.linkedUpload.openInNewTab',
                  'Open linked upload in new tab'
                )}
              >
                <IconExternalLink size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        );
      },
      Filter: ({ column }) => {
        const filterValue = column.getFilterValue();
        let selectedValues: string[] = [];

        // `intakeItemID` can be restored as either a single string (URL/deep-link)
        // or an array (MultiSelect interaction), so normalize to string[].
        if (Array.isArray(filterValue)) {
          selectedValues = filterValue.filter(
            (value): value is string =>
              typeof value === 'string' && value.trim() !== ''
          );
        } else if (
          typeof filterValue === 'string' &&
          filterValue.trim() !== ''
        ) {
          // MultiSelect expects an array even for a single selected upload.
          selectedValues = [filterValue];
        }

        return (
          <LinkedUploadFilterSelect
            value={selectedValues}
            onChange={(nextValues) => {
              column.setFilterValue(nextValues.length ? nextValues : undefined);
            }}
          />
        );
      },
      Edit: () => <ModalUploadLinkInput disabled={editDisabled} />,
    },
    {
      accessorKey: 'referringProvider',
      header: 'Referring Provider',
      accessorFn: (row) => {
        const rp = row.referringProviderObject;
        const rpNum = (row.referringProvider ?? '').trim();
        const OMIT_TITLE_FOR = ['04500', '04000'];
        // If there's no object, there are no names to show
        // Also covers the case of '00000'
        if (!rp) return '';
        // If there is an object, build name
        const showTitle = !OMIT_TITLE_FOR.includes(rpNum);
        return showTitle ? getUserNameWithTitlePrefix(rp) : getUserFullName(rp);
      },
      Cell: ({ cell }) => {
        return <Text>{cell.getValue() as string}</Text>;
      },
      Edit: () => {
        const { form, isBulk, prefix } = useActiveForm();
        const fieldPath = isBulk
          ? `${prefix}.referringProviderObject`
          : 'referringProviderObject';

        const providerObj = getValueByPath(form.getValues(), fieldPath) as
          | Provider
          | undefined;

        const label = providerObj
          ? `${providerObj.firstName} ${providerObj.lastName}`
          : '';

        return (
          <ProviderSearch
            defaultValue={label}
            payors={payorsProp}
            required={false}
            disabled={editDisabled}
          />
        );
      },
    },
    ...providerPrototypeColumns,
    ...(!isProvider ? [remittanceDateColumn] : []),
    ...tariffCommentColumns,
    {
      accessorKey: 'providerComments',
      header: 'Diagnosis',
      Edit: () => {
        return (
          <ModalTextArea
            label="Provider Comments"
            required={false}
            accessorKey="providerComments"
          />
        );
      },
    },
    {
      //TODO: change column in db to MHSC Remarks?
      accessorKey: 'clerkComments',
      header: 'MHSC Remarks',
      Edit: () => {
        return (
          <ModalTextArea
            label="MHSC Remarks"
            required={false}
            accessorKey="clerkComments"
            disabled={editDisabled}
          />
        );
      },
    },
    {
      accessorKey: 'claimComments',
      enableEditing: false,
      header: translate(
        'pages:claims.table.columns.internalComments.header',
        'Internal Comments'
      ),
      accessorFn: (row) => row.internalCommentSummary?.latestText ?? '',
      filterVariant: 'text',
      mantineFilterTextInputProps: {
        maxLength: 200,
        placeholder: translate(
          'pages:claims.table.columns.internalComments.filterPlaceholder',
          'Search internal comments (3+ characters)'
        ),
      },
      Cell: ({ row }) => {
        if (isClaimTariffRow(row.original)) return null;
        const summary = row.original.internalCommentSummary;
        if (!summary) return null;
        const moreCommentCount = summary.count - 1;
        return (
          <>
            <Text size="sm" lineClamp={2} style={{ overflowWrap: 'anywhere' }}>
              {summary.latestText}
            </Text>
            {moreCommentCount > 0 && (
              <Text size="xs" c="dimmed">
                {translate(
                  'pages:claims.table.columns.internalComments.more',
                  { count: moreCommentCount },
                  `${moreCommentCount} more ${
                    moreCommentCount === 1 ? 'comment' : 'comments'
                  }`
                )}
              </Text>
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'providerNotes',
      header: 'Chart Notes',
      Edit: () => (
        <ModalTextArea
          label="Provider Notes"
          required={false}
          accessorKey="providerNotes"
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'tariffs.icd9',
      header: 'ICD9 Codes',
      accessorFn: (row) => {
        return (row.tariffs || [])?.map((tariff) => tariff?.icd9).join(', ');
      },
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: (row) => getTariffFieldValues(row, (tariff) => tariff.icd9),
          match: matchAllTextFilters,
        }),
      },
      Edit: () => {
        const { form, prefix, isBulk } = useActiveForm();
        if (!isBulk) return null;

        const fieldPath = prefix ? `${prefix}.icd9` : 'icd9';
        const providerID = getValueByPath(
          form.getValues(),
          `${prefix}.providerID`
        );
        const selectedICD9 = getValueByPath(form.getValues(), fieldPath) as
          | string
          | undefined;

        return (
          <DiagnosisSearch
            label={translate(
              'pages:claims.bulkEdit.icd9',
              'ICD9 diagnosis for selected claims'
            )}
            defaultValue={selectedICD9 ?? ''}
            onChange={(_, icd9) => form.setFieldValue(fieldPath, icd9)}
            disabled={editDisabled}
            providerID={providerID}
          />
        );
      },
      Cell: ({ row }) => {
        return (
          <Box>
            <Group gap={4} mt={4} wrap="wrap">
              {(row.original.tariffs ?? []).map((tariff, index) => {
                const icd9 = tariff.icd9;
                return icd9 ? (
                  <ICD9Badge key={tariff.id ?? index} icd9={icd9} />
                ) : null;
              })}
            </Group>
          </Box>
        );
      },

      size: 120,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      size: 320,
      minSize: 320,
      accessorFn: (row) => {
        const createdAtTimezone =
          isProvider && isAllSites
            ? siteProp.find((site) => site.id === row.siteID)?.timezone ??
              timezone
            : timezone;

        return formatDateTime(row.createdAt, {
          timezone: createdAtTimezone,
        });
      },
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text style={{ textAlign: 'center' }}>
            {cell.getValue() as string}
          </Text>
        ),
      sortingFn: (rowA, rowB) =>
        compareDateAsc(rowA.original.createdAt, rowB.original.createdAt),
      filterVariant: 'date-range',
      mantineFilterDateInputProps: {
        valueFormat: 'YYYY-MM-DD',
        clearable: true,
        max: nowISO(),
      },
    },
    {
      accessorKey: 'updatedAt',
      header: translate(
        'pages:claims.table.columns.lastUpdatedAt',
        'Last Updated At'
      ),
      accessorFn: (row) =>
        formatDateTime(row.updatedAt, {
          timezone:
            row.site?.timezone ??
            siteProp.find((site) => site.id === row.siteID)?.timezone ??
            timezone,
        }),
      enableEditing: false,
      enableColumnFilter: false,
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text style={{ textAlign: 'center' }}>{cell.getValue<string>()}</Text>
        ),
      sortingFn: (rowA, rowB) =>
        compareDateAsc(rowA.original.updatedAt, rowB.original.updatedAt),
    },
    {
      accessorKey: 'createdByUser',
      header: 'Created By',
      accessorFn: (row) => {
        const user = row.createdByUser;
        if (!user) return '';
        return getUserFullName(user);
      },
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (!value) return null;
        if (typeof value === 'string') {
          return <Text>{value}</Text>;
        }
        // Defensive: render user objects safely if they reach the cell
        const user = value as {
          firstName?: string | null;
          lastName?: string | null;
        };
        return <Text>{getUserFullName(user as any)}</Text>;
      },
      filterVariant: 'text',
      mantineFilterTextInputProps: {
        placeholder: 'Search creator',
      },
    },
    {
      //TODO: rename column to chiropracticServiceCode
      accessorKey: 'chiroService',
      header: 'Chiropractic Service Code',
      Edit: () => {
        return (
          <ModalTextInput
            label="Chiropractic Service Code"
            required={false}
            accessorKey="chiroService"
            disabled={editDisabled}
          />
        );
      },
    },
    {
      accessorKey: 'interpretingRadiologist',
      header: 'Interpreting Radiologist',
      Edit: () => (
        <ModalTextInput
          label="Interpreting Radiologist"
          required={false}
          accessorKey="interpretingRadiologist"
          disabled={editDisabled}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'locationOfService',
      header: 'Location of Service',
      Edit: ({ cell }) => {
        const { form, formType, prefix } = useActiveForm();
        const currentValue = cell.getValue() as string;
        const payorPath = prefix ? `${prefix}.payor` : 'payor';
        const formPayor = getValueByPath(form.getValues(), payorPath) as
          | Payor
          | null
          | undefined;
        const payor =
          formPayor === undefined ? cell.row.original.payor : formPayor;
        const options = getLocationOfServiceOptions(payor).map((option) => ({
          ...option,
        }));
        const showBCRequirement = isBCPayor(payor);
        const isQDocSite =
          cell.row.original.site?.name === 'QDoc Virtual Healthcare';
        const showQDocRequirement = formType === 'edit' && isQDocSite;
        const showRequirement = showBCRequirement || showQDocRequirement;
        addInvalidOptions(options, currentValue);
        return (
          <ModalSelectInput
            label="Location of Service"
            options={options}
            accessorKey="locationOfService"
            required={showRequirement}
            enableTooltip={showRequirement}
            tooltipText={
              showBCRequirement
                ? 'Required for BC claims.'
                : 'Required for QDoc Virtual Healthcare claims.'
            }
            dataTestId="location-of-service-select"
            disabled={editDisabled}
          />
        );
      },
    },
    {
      accessorKey: 'virtualPhysicianRegion',
      header: 'Virtual Physician Region',
      Edit: () => {
        const options = getClaimSelectOptions(
          'virtualPhysicianRegion',
          translate
        );
        return (
          <ModalSelectInput
            label="Virtual Physician Region"
            required={false}
            accessorKey="virtualPhysicianRegion"
            options={options}
            disabled={editDisabled}
          />
        );
      },
      accessorFn: (row) => row.virtualPhysicianRegion,
    },
    {
      //TODO: rename column to optometricReasonCode
      accessorKey: 'optometristReason',
      header: 'Optometric Reason Code',
      Edit: ({ cell }) => {
        const currentValue = cell.getValue() as string;
        const options = getClaimSelectOptions('optometristReason', translate);
        addInvalidOptions(options, currentValue);
        return (
          <ModalSelectInput
            label="Optometric Reason Code"
            options={options}
            accessorKey="optometristReason"
            required={false}
            dataTestId="optometrist-reason-select"
            disabled={editDisabled}
          />
        );
      },
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'patientCount',
      header: 'Patient Count',
      Edit: () => (
        <ModalTextInput
          label="Patient Count"
          required={false}
          accessorKey="patientCount"
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'serviceLocationIndicator',
      header: 'Service Location Indicator',
      Edit: ({ cell }) => {
        const { form, prefix } = useActiveForm();
        const currentValue = cell.getValue() as string;
        const payorPath = prefix ? `${prefix}.payor` : 'payor';
        const formPayor = getValueByPath(form.getValues(), payorPath) as
          | Payor
          | null
          | undefined;
        const payor =
          formPayor === undefined ? cell.row.original.payor : formPayor;
        const options = getServiceLocationIndicatorOptions(
          payor?.province,
          translate
        );
        addInvalidOptions(options, currentValue);

        return (
          <ModalSelectInput
            label={translate(
              'pages:claims.provinceFields.serviceLocationIndicator',
              'Service Location Indicator'
            )}
            accessorKey="serviceLocationIndicator"
            dataTestId="service-location-indicator-select"
            options={options}
            required={false}
            disabled={editDisabled}
          />
        );
      },
    },
    {
      accessorKey: 'thirdPartyLiability',
      header: 'Third Party Liability',
      Edit: ({ cell }) => {
        const currentValue = cell.getValue() as string;
        const options = getClaimSelectOptions('thirdPartyLiability', translate);
        addInvalidOptions(options, currentValue);
        return (
          <ModalSelectInput
            label="Third Party Liability"
            options={options}
            accessorKey="thirdPartyLiability"
            required={false}
            dataTestId="third-party-liability-select"
            disabled={editDisabled}
          />
        );
      },
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'incorporated',
      header: 'Incorporated',
      Edit: ({ cell }) => {
        const currentValue = cell.getValue() as string;
        const options = getClaimSelectOptions('incorporated', translate);
        addInvalidOptions(options, currentValue);
        return (
          <ModalSelectInput
            label="Incorporated"
            options={options}
            accessorKey="incorporated"
            required={false}
            dataTestId="incorporated-select"
            disabled={editDisabled}
          />
        );
      },
    },
    {
      accessorKey: 'claimType',
      header: translate('pages:claims.provinceFields.claimType', 'Claim Type'),
      Edit: ({ cell }) => (
        <SKClaimFieldSelect
          field="claimType"
          labelKey="pages:claims.provinceFields.claimType"
          defaultLabel="Claim Type"
          dataTestId="claim-type-select"
          currentValue={cell.getValue() as string | null | undefined}
          rowPayor={cell.row.original.payor}
          disabled={editDisabled}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'claimMode',
      header: translate('pages:claims.provinceFields.claimMode', 'Claim Mode'),
      Edit: ({ cell }) => (
        <SKClaimFieldSelect
          field="claimMode"
          labelKey="pages:claims.provinceFields.claimMode"
          defaultLabel="Claim Mode"
          dataTestId="claim-mode-select"
          currentValue={cell.getValue() as string | null | undefined}
          rowPayor={cell.row.original.payor}
          disabled={editDisabled}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'formType',
      header: translate('pages:claims.provinceFields.formType', 'Form Type'),
      Edit: ({ cell }) => (
        <SKClaimFieldSelect
          field="formType"
          labelKey="pages:claims.provinceFields.formType"
          defaultLabel="Form Type"
          dataTestId="form-type-select"
          currentValue={cell.getValue() as string | null | undefined}
          rowPayor={cell.row.original.payor}
          disabled={editDisabled}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'corporationIndicator',
      header: translate(
        'pages:claims.provinceFields.corporationIndicator',
        'Corporation Indicator'
      ),
      Edit: ({ cell }) => (
        <SKClaimFieldSelect
          field="corporationIndicator"
          labelKey="pages:claims.provinceFields.corporationIndicator"
          defaultLabel="Corporation Indicator"
          dataTestId="corporation-indicator-select"
          currentValue={cell.getValue() as string | null | undefined}
          rowPayor={cell.row.original.payor}
          disabled={editDisabled}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'specialCircumstances',
      header: translate(
        'pages:claims.provinceFields.specialCircumstancesField',
        'Special Circumstances'
      ),
      Edit: ({ cell }) => (
        <SKClaimFieldSelect
          field="specialCircumstances"
          labelKey="pages:claims.provinceFields.specialCircumstancesField"
          defaultLabel="Special Circumstances"
          dataTestId="special-circumstances-select"
          currentValue={cell.getValue() as string | null | undefined}
          rowPayor={cell.row.original.payor}
          disabled={editDisabled}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'hospitalCode',
      header: 'Hospital Name',
      accessorFn: (row) => {
        const hospital = hospitalOptions.find(
          (h) => h.value === row.hospitalCode
        );
        return hospital ? hospital.label : row.hospitalCode;
      },
      Edit: () => {
        const { form, isBulk, prefix } = useActiveForm();

        const serviceLocationPath = isBulk
          ? `${prefix}.serviceLocationIndicator`
          : 'serviceLocationIndicator';
        const hospitalCodePath = isBulk
          ? `${prefix}.hospitalCode`
          : 'hospitalCode';
        const hospitalNamePath = isBulk
          ? `${prefix}.hospitalName`
          : 'hospitalName';
        const locationOfServicePath = isBulk
          ? `${prefix}.locationOfService`
          : 'locationOfService';
        const payorPath = isBulk ? `${prefix}.payor` : 'payor';

        const serviceLocationIndicator = getValueByPath(
          form.getValues(),
          serviceLocationPath
        ) as string | null;

        const hasServiceLocationIndicator =
          !!serviceLocationIndicator && serviceLocationIndicator !== ' ';
        const payor = getValueByPath(
          form.getValues(),
          payorPath
        ) as Payor | null;

        // Keep hospitalName in sync with hospitalCode
        form.watch(hospitalCodePath, ({ value }) => {
          syncHospitalSelectionToClaimLocation({
            hospitalCode: value,
            hospitalNamePath,
            locationOfServicePath,
            payor,
            setFieldValue: form.setFieldValue,
          });
        });

        return (
          <ModalSelectInput
            options={hospitalOptions}
            dataTestId="hospital-code-select"
            label="Hospital Name"
            required={hasServiceLocationIndicator}
            accessorKey="hospitalCode"
            disabled={editDisabled}
          />
        );
      },
      mantineTableBodyCellProps: {
        style: { maxWidth: '3rem' },
      },
    },
    {
      accessorKey: 'facilityNumber',
      header: 'Facility Number',
      Edit: () => (
        <ModalTextInput
          label="Facility Number"
          required={false}
          accessorKey="facilityNumber"
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'site.siteNumber',
      header: 'Site Number',
      Edit: () => {
        return (
          <ModalTextInput
            label="Site Number"
            required={false}
            accessorKey="site.siteNumber"
            disabled={true}
          />
        );
      },
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'siteID',
      header: 'Site',
      Cell: ({ cell }) => (
        <Badge color="teal" variant="filled">
          {siteProp.find((site) => site.id === cell.getValue())?.name}
        </Badge>
      ),
    },
    /************** Claim Fields ***********************/
    /************** RELATIONS**********************/
    //TODO: readd edit functionality
    {
      accessorKey: 'patientID',
      header: 'Patient',
      accessorFn: (row) => getPatientSearchValue(row),
      enableGlobalFilter: true,
      Cell: ({ row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Box className="claims-patient-cell">
            <Text fw={700} size="sm">
              {row.original.patient
                ? formatPatientName(
                    row.original.patient.firstName,
                    row.original.patient.lastName
                  )
                : ''}
            </Text>
            <Text size="xs" c={mutedColor}>
              {row.original.patient?.dateOfBirth
                ? row.original.patient.dateOfBirth
                : '—'}
            </Text>
          </Box>
        ),
    },
    {
      accessorKey: 'healthcard.number',
      header: 'Healthcard',
      accessorFn: (row) => getPhinSearchValue(row),
      Edit: () => (
        <ModalTextInput
          label="PHIN"
          required={true}
          accessorKey="healthcard.number"
          disabled={editDisabled}
        />
      ),
      Cell: ({ row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Box className="claims-phin-cell">
            <Text fw={600} size="sm">
              {row.original.healthcard?.number ?? ''}
            </Text>
            <Text size="xs" c={mutedColor}>
              {row.original.healthcard?.registration || '—'}
            </Text>
          </Box>
        ),
      size: 10,
    },

    {
      accessorKey: 'providerID',
      header: 'Provider',
      accessorFn: (row) => getProviderSearchValue(row),
      Cell: ({ row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Box className="claims-provider-cell">
            <Text fw={600} size="sm">
              {row.original.provider
                ? getUserNameWithTitlePrefix(row.original.provider)
                : ''}
            </Text>
            <Text size="xs" c={mutedColor}>
              {row.original.provider?.specialty?.description}
            </Text>
          </Box>
        ),
      enableColumnFilter: true,
      renderColumnActionsMenuItems: ({ internalColumnMenuItems, table }) => (
        <>
          <Menu.Item
            onClick={() => {
              const currentFilters = table
                .getState()
                .columnFilters.filter((filter) => filter.id !== 'providerID');
              table.setColumnFilters([
                ...currentFilters,
                { id: 'providerID', value: 'Dr.' },
              ]);
            }}
          >
            Filter by 'Dr.'
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              const currentFilters = table
                .getState()
                .columnFilters.filter((filter) => filter.id !== 'providerID');
              table.setColumnFilters([
                ...currentFilters,
                { id: 'providerID', value: 'NP.' },
              ]);
            }}
          >
            Filter by 'Np.'
          </Menu.Item>
          <Divider />
          {internalColumnMenuItems}
        </>
      ),
    },
    {
      accessorKey: 'tariffs',
      header: 'Tariffs',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: getTariffSearchValue,
          match: matchAllTextFilters,
        }),
      },
      Cell: ({ row }) => {
        if (isClaimTariffRow(row.original)) {
          const rowOriginal = row.original;
          const tariff = rowOriginal.tariffs?.[0];
          if (!tariff) return null;
          const statusChip = getTariffCodeBadgePresentation(
            getTariffStatus(rowOriginal, tariff)
          );
          return (
            <Badge
              size="sm"
              radius="xl"
              variant="light"
              style={{
                backgroundColor: statusChip.backgroundColor,
                border: `1px solid ${statusChip.borderColor}`,
                color: statusChip.textColor,
              }}
            >
              {`${tariff.prefix}${tariff.code}`}
            </Badge>
          );
        }
        return (
          <Box className="claims-tariffs-cell">
            <Group gap={4} mt={4} wrap="wrap">
              {(row.original.tariffs ?? []).map((tariff) => {
                const statusChip = getTariffCodeBadgePresentation(
                  getTariffStatus(row.original, tariff as Tariff)
                );

                return (
                  <Badge
                    key={tariff.id}
                    size="sm"
                    radius="xl"
                    variant="light"
                    style={{
                      backgroundColor: statusChip.backgroundColor,
                      border: `1px solid ${statusChip.borderColor}`,
                      color: statusChip.textColor,
                    }}
                  >
                    {`${tariff.prefix}${tariff.code}`}
                  </Badge>
                );
              })}
            </Group>
          </Box>
        );
      },
      Edit: () => {
        return (
          <ModalTariffSelect
            dirtyFields={dirtyFields}
            stagedTariffActionsRef={stagedTariffActionsRef}
            disabled={editDisabled}
          />
        );
      },
      minSize: 180,
    },
    {
      accessorKey: 'status',
      accessorFn: (row) => getRowDisplayStatus(row, { isProvider }),
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          includeColumnID: false,
          aliases: ['tariffs.status'],
          getValue: (row) => {
            const tariff = row.tariffs[0];
            return tariff ? getTariffStatus(row, tariff) : '';
          },
          match: matchAnyExactFilter,
        }),
      },
      header: 'Status',
      Edit: () => {
        const options = claimStatusEnumValues.map((status) => ({
          label: status,
          value: status,
        }));
        return (
          <ModalSelectInput
            label="Status"
            required={false}
            accessorKey="status"
            options={options}
            disabled={editDisabled}
          />
        );
      },
      filterVariant: 'select',
      Filter: () => <ClaimStatusFilter {...statusFilterProps} />,
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        const isTariffRow = isClaimTariffRow(cell.row.original);

        if (isProvider) {
          if (isTariffRow) {
            const rowOriginal = cell.row.original;
            const tariff = rowOriginal.tariffs?.[0];
            if (!tariff) return null;
            const tariffStatus = getTariffStatus(rowOriginal, tariff as Tariff);
            const presentation = getProviderStatusPresentation(
              tariffStatus,
              computedColorScheme
            );
            if (presentation.label === CLAIM_TARIFF_STATUS.CANCELLED) {
              return <Text size="sm">{presentation.label}</Text>;
            }
            return renderStatusBadge({
              label: presentation.label,
              tooltip:
                tariffStatus === CLAIM_TARIFF_STATUS.UNCLAIMABLE
                  ? tariff.latestComment?.trim() ||
                    translate(
                      'pages:claims.unclaimableCommentFallback',
                      'This tariff was rejected and correctly assessed.'
                    )
                  : presentation.tooltip,
              provider: true,
            });
          }

          if (value === CLAIM_TARIFF_STATUS.CANCELLED) {
            return <Text size="sm">{value}</Text>;
          }
          return renderStatusBadge({
            label: value,
            provider: true,
          });
        }

        return renderStatusBadge({ label: value });
      },
    },
    {
      accessorKey: 'payorID',
      header: 'Payor',
      accessorFn: (row) => row.payor?.name,
      Header: ({ table }) => {
        const activePayorType = getActivePayorType(table);

        const payorTypeTooltip =
          activePayorType === PayorType.PRIVATE
            ? translate(
                'pages:claims.table.columns.payor.activePrivate',
                'Payor type filter: Private'
              )
            : activePayorType === PayorType.PUBLIC
            ? translate(
                'pages:claims.table.columns.payor.activePublic',
                'Payor type filter: Public'
              )
            : translate(
                'pages:claims.table.columns.payor.activeDirect',
                'Payor type filter: Direct'
              );

        return (
          <Group gap={6} wrap="nowrap">
            <span>
              {translate('pages:claims.table.columns.payor.header', 'Payor')}
            </span>
            {activePayorType ? (
              <Tooltip label={payorTypeTooltip}>
                <ActionIcon
                  aria-label={translate(
                    'pages:claims.table.columns.payor.openFilters',
                    'Open payor filters'
                  )}
                  className="mrt-table-head-cell-filter-label-icon"
                  size={18}
                  variant="transparent"
                  data-active
                  onClick={(event) => {
                    event.stopPropagation();
                    table.setShowColumnFilters(true);
                    setTimeout(() => {
                      const input =
                        table.refs.filterInputRefs.current['payorID-0'];
                      input?.focus();
                    }, 100);
                  }}
                  style={
                    {
                      '--ai-bg': 'transparent',
                      '--ai-hover':
                        computedColorScheme === 'dark'
                          ? 'var(--mantine-color-dark-6)'
                          : 'var(--mantine-color-gray-1)',
                      '--ai-color': 'var(--mantine-color-white)',
                      '--ai-bd':
                        'calc(0.0625rem * var(--mantine-scale)) solid transparent',
                      '--ai-hover-color': 'var(--mantine-color-bright)',
                      color: 'var(--mantine-primary-color-filled)',
                    } as React.CSSProperties
                  }
                >
                  <IconFilter size="100%" />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Group>
        );
      },
      renderColumnActionsMenuItems: ({ internalColumnMenuItems, table }) => {
        // Store the active filter value for payor type
        const activePayorType = getActivePayorType(table);

        // Function to apply the payor type filter
        const applyPayorTypeFilter = (value: PayorType) => {
          const currentFilters = table
            .getState()
            .columnFilters.filter((filter) => filter.id !== 'payor.type');
          table.setColumnFilters([
            ...currentFilters,
            { id: 'payor.type', value },
          ]);
        };

        // Function to clear the payor type filter
        const clearPayorTypeFilter = () => {
          const currentFilters = table
            .getState()
            .columnFilters.filter((filter) => filter.id !== 'payor.type');
          table.setColumnFilters(currentFilters);
        };

        return (
          <>
            <Menu.Item
              leftSection={
                activePayorType === PayorType.PRIVATE ? (
                  <IconCheck size={16} />
                ) : null
              }
              fw={activePayorType === PayorType.PRIVATE ? 700 : undefined}
              onClick={() => applyPayorTypeFilter(PayorType.PRIVATE)}
            >
              {translate(
                'pages:claims.table.columns.payor.filterByPrivate',
                'Filter by Private'
              )}
            </Menu.Item>
            <Menu.Item
              leftSection={
                activePayorType === PayorType.PUBLIC ? (
                  <IconCheck size={16} />
                ) : null
              }
              fw={activePayorType === PayorType.PUBLIC ? 700 : undefined}
              onClick={() => applyPayorTypeFilter(PayorType.PUBLIC)}
            >
              {translate(
                'pages:claims.table.columns.payor.filterByPublic',
                'Filter by Public'
              )}
            </Menu.Item>
            <Menu.Item
              leftSection={
                activePayorType === PayorType.DIRECT ? (
                  <IconCheck size={16} />
                ) : null
              }
              fw={activePayorType === PayorType.DIRECT ? 700 : undefined}
              onClick={() => applyPayorTypeFilter(PayorType.DIRECT)}
            >
              {translate(
                'pages:claims.table.columns.payor.filterByDirect',
                'Filter by Direct'
              )}
            </Menu.Item>
            <Menu.Item
              leftSection={!activePayorType ? <IconCheck size={16} /> : null}
              fw={!activePayorType ? 700 : undefined}
              onClick={clearPayorTypeFilter}
            >
              {translate(
                'pages:claims.table.columns.payor.clearTypeFilter',
                'Clear Payor Type Filter'
              )}
            </Menu.Item>
            <Divider />
            {internalColumnMenuItems}
          </>
        );
      },
      Edit: () => (
        <ModalSelectInput
          label="Payor"
          accessorKey="payorID"
          options={payorsProp.map((payor) => {
            return { label: payor?.name, value: payor?.id };
          })}
          disabled={editDisabled}
        />
      ),
      filterVariant: 'multi-select',
      mantineFilterMultiSelectProps: {
        clearable: true,
        clearButtonProps: {
          style: { display: 'none' },
        },
        data: payorsProp.map((payor) => ({
          value: payor?.id,
          label: payor?.name,
        })),
      },
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
    },
    /************** RELATIONS **********************/
    /************** Patient Fields ****************/
    {
      accessorKey: 'debits.eobs',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: getLatestDebitEOBCodeFilterValue,
          match: matchLatestEOBCodeFilters,
        }),
      },
      header: 'EOB Codes',
      visibleInShowHideMenu: false,
      Edit: ({ row }) => {
        const eobs = groupDebitEOBs(row.original.debits || []);

        return eobs.map((eob) => (
          <Group key={`${eob.code}-${eob.id}`} gap="xl">
            <EOBCard key={eob.id} eob={eob} opened={true} />
          </Group>
        ));
      },
      Cell: ({ row }) => {
        const eobs = groupDebitEOBs(row.original.debits || []);
        return (
          <EOBCodesCell
            eobs={eobs}
            borderColor={
              computedColorScheme === 'dark'
                ? theme.colors.dark[3]
                : theme.colors.gray[2]
            }
            mutedColor={mutedColor}
          />
        );
      },
      filterVariant: 'select',
      Filter: ({ column }) => {
        const value = column.getFilterValue();
        return (
          <ClaimTableSelectFilter
            data={uniqueEobs.map((eob) => ({
              value: eob.code,
              label: eob.code,
            }))}
            value={typeof value === 'string' ? value : null}
            onChange={(nextValue) =>
              column.setFilterValue(nextValue ?? undefined)
            }
            onDropdownOpen={onEobFilterOpen}
            placeholder={translate(
              'pages:claims.tableSelectFilter.eobPlaceholder',
              'Filter by EOB'
            )}
            searchable
          />
        );
      },
    },
    {
      accessorKey: 'debits.requestedAmount',
      header: 'Billed',
      accessorFn: (row) => getClaimRequestedAmount(row),
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: getClaimRequestedAmount,
          match: matchAllNumericFilters,
        }),
      },
      Cell: ({ cell }) => (
        <Text fw={600} size="sm">
          {formatCurrency(Number(cell.getValue()))}
        </Text>
      ),
    },
    {
      accessorKey: 'debits.paidAmount',
      header: 'Paid',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: getLatestDebitPaidAmountFilterValue,
          match: matchPaidAmountFilters,
        }),
      },
      accessorFn: getClaimPaidAmount,
      sortingFn: 'basic',
      sortUndefined: 'last',
      Cell: ({ cell, row }) => {
        const paidAmount = cell.getValue<number | undefined>();
        if (paidAmount === undefined) {
          if (!getProviderBilledDate(row.original)) {
            return (
              <Text size="sm" c={mutedColor}>
                {translate('pages:claims.paidNotSubmitted', '–')}
              </Text>
            );
          }

          return (
            <Tooltip
              multiline
              w={220}
              withArrow
              label={translate(
                'pages:claims.remittanceInProgressTooltip',
                'Remittance is still in progress with the payor. The amount will update once remittance is received.'
              )}
            >
              <Text size="sm">
                {translate(
                  'pages:claims.remittanceInProgress',
                  'Remittance In Progress'
                )}
              </Text>
            </Tooltip>
          );
        }

        return (
          <Box className="claims-amount-cell">
            <Text fw={700} size="sm">
              {formatCurrency(paidAmount)}
            </Text>
          </Box>
        );
      },
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'debits.differenceAmount',
      header: 'Difference',
      accessorFn: (row) => getClaimDifferenceAmount(row),
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: getLatestDebitDifferenceAmountFilterValue,
          match: matchAllNumericFilters,
        }),
      },
      Cell: ({ cell }) => {
        const difference = Number(cell.getValue());
        return (
          <Text fw={600} size="sm">
            {formatCurrency(difference)}
          </Text>
        );
      },
    },
    {
      accessorKey: 'debits.eob.description',
      header: 'EOB Description',
      visibleInShowHideMenu: false,
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: getLatestDebitEOBDescriptionFilterValue,
          match: matchAllTextFilters,
        }),
      },
      accessorFn: (row) => {
        return row.debits
          ? row.debits
              .map((debit) =>
                debit.eobs ? debit.eobs.map((eob) => eob.description) : []
              )
              .flat()
              .join(', ')
          : '';
      },
    },
    {
      accessorKey: 'debits.microfilm',
      header: 'Microfilm Number',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: getLatestDebitMicrofilmFilterValue,
          match: matchAllTextFilters,
        }),
      },
      accessorFn: (row) => {
        const microfilms = row.debits
          ? row.debits
              .map((debit) => debit.microfilm)
              .filter((microfilm): microfilm is string => !!microfilm)
          : [];
        return microfilms.join(', ');
      },
    },
    {
      accessorKey: 'messageID',
      header: 'Source System Identifier',
      visibleInShowHideMenu: false,
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: ({ cell }) => (
        <ModalDataText
          value={cell.getValue() as string}
          title="Source System Identifier"
          accessorKey="messageID"
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.firstName',
      header: 'Patient First Name',
      visibleInShowHideMenu: false,
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="First Name"
          required={true}
          accessorKey="patient.firstName"
          capitalizeFirst={true}
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.lastName',
      header: 'Patient Last Name',
      visibleInShowHideMenu: false,
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="Last Name"
          required={true}
          accessorKey="patient.lastName"
          capitalizeFirst={true}
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.address',
      header: 'Patient Address',
      visibleInShowHideMenu: false,
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="Address"
          required={false}
          accessorKey="patient.address"
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.city',
      header: 'Patient City',
      visibleInShowHideMenu: false,
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="City"
          required={false}
          accessorKey="patient.city"
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.province',
      header: 'Patient Province',
      visibleInShowHideMenu: false,
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalSelectInput
          label="Province"
          options={provinceOptions.map((province) => ({
            label: province,
            value: province,
          }))}
          accessorKey="patient.province"
          required={false}
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.country',
      header: 'Patient Country',
      visibleInShowHideMenu: false,
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="Country"
          required={false}
          accessorKey="patient.country"
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.dateOfBirth',
      header: 'Date of Birth',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalDateInput
          label="DOB"
          accessorKey="patient.dateOfBirth"
          required={true}
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.sex',
      header: 'Sex',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalSelectInput
          accessorKey="patient.sex"
          label="Sex"
          required={true}
          options={[
            { label: 'Male', value: 'M' },
            { label: 'Female', value: 'F' },
            { label: 'Other', value: 'X' },
          ]}
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.postalCode',
      header: 'Patient Postal Code',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="Postal Code"
          required={false}
          accessorKey="patient.postalCode"
          disabled={editDisabled}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'patient.phone',
      header: 'Patient Cellphone',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="Cellphone"
          required={false}
          accessorKey="patient.phone"
          disabled={editDisabled}
        />
      ),
    },
    {
      accessorKey: 'patient.organizationID',
      header: 'Patient Organization',
      Cell: ({ row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : row.original.patient?.organization ? (
          <Pill>{row.original.patient.organization.name}</Pill>
        ) : null,
      Edit: ({ row }) => {
        const { form, prefix } = useActiveForm();
        const fieldPath = prefix
          ? `${prefix}.patient.organizationID`
          : 'patient.organizationID';
        const patientOrg: Organization =
          row.original.patient?.organization || organization;
        const options = [
          { label: patientOrg?.name || 'N/A', value: patientOrg?.id },
        ];

        // Only runs when there's a valid single option and current value differs.
        useEffect(() => {
          if (
            options[0].value &&
            getValueByPath(form.getValues(), fieldPath) !== options[0].value
          ) {
            form.setFieldValue(fieldPath, options[0].value);
          }
        }, [fieldPath, options[0].value]);

        return (
          <ModalSelectInput
            accessorKey="patient.organizationID"
            label="Organization"
            required={false}
            options={options}
            disabled={editDisabled}
          />
        );
      },
    },
    /************ Patient Fields ****************/
    /************** Provider Fields ****************/
    {
      accessorKey: 'provider.firstName',
      header: 'Provider First Name',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="First Name"
          required={false}
          accessorKey="provider.firstName"
          disabled={true}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'provider.lastName',
      header: 'Provider Last Name',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextInput
          label="Last Name"
          required={false}
          accessorKey="provider.lastName"
          disabled={true}
        />
      ),
      visibleInShowHideMenu: false,
    },
    {
      header: 'Billing Number',
      accessorKey: 'provider.licenses',
      accessorFn: (row) => {
        const payor = payorsProp?.find(
          (payor: Payor) => payor.id === row.payorID
        );
        const license = row.provider?.licenses?.find(
          (license) => license.payorID === payor?.id
        );
        return license ? license.license : '';
      },
      Edit: () => (
        <ModalBillingNumberInput
          label="Billing Number"
          required={false}
          accessorKey="provider.licenses"
        />
      ),
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
    },
    {
      accessorKey: 'provider.specialtyCode',
      header: 'Specialty Code',
      accessorFn: (row) => {
        const code = row.provider?.specialty?.code;
        const description = row.provider?.specialty?.description;
        if (!code || !description) return '';
        return `${code} - ${description}`;
      },
      Edit: ({ row }) => {
        const options = getSpecialtyCodes(row.original.provider?.province).map(
          (specialty) => ({
            label: `${specialty.code} - ${specialty.description}`,
            value: specialty.code,
          })
        );

        return (
          <ModalSelectInput
            label="Specialty Code"
            required={false}
            accessorKey="provider.specialtyCode"
            options={options}
            disabled={true}
          />
        );
      },
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
    },
    {
      accessorKey: 'provider.contactNote',
      accessorFn: (row) => row.provider?.contactNote || '',
      header: 'Provider Contact Note',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => (
        <ModalTextArea
          label="Provider Contact Note"
          required={false}
          accessorKey="provider.contactNote"
          disabled={true}
        />
      ),
    },
    /************** Provider Fields ****************/
    /************** Tariff Fields ****************/
    {
      accessorKey: 'tariffs.code',
      accessorFn: (row) =>
        row?.tariffs
          ?.map((tariff) => `${tariff.prefix}${tariff.code}`)
          .join(', '),
      header: 'Tariff Code',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: (row) =>
            getTariffFieldValues(
              row,
              (tariff) => `${tariff.prefix}${tariff.code}`
            ),
          match: matchAllTextFilters,
        }),
      },
    },
    {
      accessorKey: 'tariffs.description',
      accessorFn: (row) =>
        row?.tariffs?.map((tariff) => tariff.description).join(', '),
      header: 'Tariff Description',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: (row) =>
            getTariffFieldValues(row, (tariff) => tariff.description),
          match: matchAllTextFilters,
        }),
      },
    },
    {
      accessorKey: 'tariffs.type',
      accessorFn: (row) =>
        row?.tariffs?.map((tariff) => tariff.type).join(', '),
      header: 'Tariff Type',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: (row) => getTariffFieldValues(row, (tariff) => tariff.type),
          match: matchAllTextFilters,
        }),
      },
    },
    {
      accessorKey: 'tariffs.rate',
      accessorFn: (row) =>
        row?.tariffs?.map((tariff) => tariff.rate).join(', '),
      header: 'Tariff Rate',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: (row) => getTariffFieldValues(row, (tariff) => tariff.rate),
          match: matchAllNumericFilters,
        }),
      },
    },
    {
      accessorKey: 'tariffs.units',
      accessorFn: (row) =>
        isClaimTariffRow(row)
          ? row.tariffs?.[0]?.units ?? ''
          : row?.tariffs?.map((tariff) => tariff.units ?? '').join(', '),
      header: 'Tariff Units',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: (row) =>
            getTariffFieldValues(row, (tariff) => tariff.units),
          match: matchAllNumericFilters,
        }),
      },
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          <Text size="sm">{String(cell.getValue() ?? '')}</Text>
        ) : (
          renderClaimLevelPlaceholder(mutedColor)
        ),
    },
    {
      accessorKey: 'tariffs.split',
      accessorFn: (row) =>
        isClaimTariffRow(row)
          ? row.tariffs?.[0]?.split ?? ''
          : row?.tariffs?.map((tariff) => tariff.split ?? '').join(', '),
      header: 'Split Indicator',
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: (row) =>
            getTariffFieldValues(row, (tariff) => tariff.split),
          match: matchAllTextFilters,
        }),
      },
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          <Text size="sm">{String(cell.getValue() ?? '')}</Text>
        ) : (
          renderClaimLevelPlaceholder(mutedColor)
        ),
    },
    {
      accessorKey: 'tariffs.incision',
      accessorFn: (row) =>
        isClaimTariffRow(row) ? row.tariffs?.[0]?.incision ?? '' : '',
      header: translate(
        'pages:claims.table.columns.incision',
        'Incision'
      ) as string,
      meta: {
        tariffFilter: defineClaimTariffFilter({
          scope: 'tariff',
          getValue: (row) =>
            getTariffFieldValues(row, (tariff) => tariff.incision),
          match: matchAllTextFilters,
        }),
      },
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          <Text size="sm">{String(cell.getValue() ?? '')}</Text>
        ) : (
          renderClaimLevelPlaceholder(mutedColor)
        ),
    },
    /************** Tariff Fields ****************/
    /************** Claim Type Fields ****************/
    /************** Healthcard Fields ***************/
    {
      accessorKey: 'healthcard.registration',
      header: 'Registration Number',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: () => {
        const { form, isBulk, prefix } = useActiveForm();
        const signingAuthorityPath = isBulk
          ? `${prefix}.healthcard.province`
          : 'healthcard.province';

        const signingAuthority = getValueByPath(
          form.getValues(),
          signingAuthorityPath
        ) as string | null;

        const isRegistrationRequired = signingAuthority === 'MB';

        return (
          <ModalTextInput
            label="Registration Number"
            required={isRegistrationRequired}
            accessorKey="healthcard.registration"
            disabled={editDisabled}
          />
        );
      },
    },

    {
      accessorKey: 'healthcard.province',
      header: 'Signing Authority',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      Edit: ({ cell }) => {
        const currentValue = cell.getValue() as string;
        const signingAuthorities = healthcardSigningAuthorityOptions.map(
          (group) => ({
            ...group,
            items: [...group.items],
          })
        );

        const hasCurrentValue = signingAuthorities.some((group) =>
          group.items.some((item) => item.value === currentValue)
        );

        if (!hasCurrentValue && currentValue && currentValue !== '') {
          signingAuthorities.push({
            group: 'Unknown',
            items: [
              {
                label: currentValue,
                value: currentValue,
                disabled: true,
              },
            ],
          });
        }

        return (
          <ModalGroupedSelectInput
            label="Signing Authority"
            required={true}
            accessorKey="healthcard.province"
            options={signingAuthorities}
            disabled={editDisabled}
          />
        );
      },
    },
    {
      // TODO: Figure out if this column is still needed
      accessorFn: (row) =>
        claimHasAnyTariffStatus(row, [
          CLAIM_TARIFF_STATUS.REJECTED,
          ...UNRECONCILED_CLAIM_TARIFF_STATUSES,
        ]),
      header: 'Resubmit',
      accessorKey: 'resubmit',
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'remittances',
      header: 'Remittance',
      accessorFn: (row) =>
        row.remittances?.map((remittance) => remittance.filename).join(', '),
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text size="sm">{(cell.getValue() as string) || ''}</Text>
        ),
      filterVariant: 'select',
      mantineFilterSelectProps: {
        data: remittancesProp?.map((remittance) => {
          return {
            value: remittance.id,
            label: remittance.filename,
          };
        }),
      },
    },
    /*************Submission Fields ***************  */
    {
      accessorKey: 'submissions.submittedAt',
      header: 'Last Submitted At',
      accessorFn: (row) => {
        const lastSubmission = row?.submissions?.sort((a, b) =>
          compareDateDesc(a.submittedAt, b.submittedAt)
        )[0];
        if (lastSubmission) {
          return (
            formatDateTime(lastSubmission.submittedAt, { timezone }) ||
            formatDateTime(nowISO(timezone), { timezone })
          );
        } else {
          return '';
        }
      },
      filterVariant: 'date-range',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Text style={{ textAlign: 'center' }}>
            {cell.getValue() as string}
          </Text>
        ),
      mantineFilterDateInputProps: {
        valueFormat: 'YYYY-MM-DD',
        clearable: true,
        max: nowISO(),
      },
    },
    {
      accessorKey: 'groupID',
      header: 'Provider Group',
      accessorFn: (row) => row.group?.name || '',
      Cell: ({ cell, row }) =>
        isClaimTariffRow(row.original) ? (
          renderClaimLevelPlaceholder(mutedColor)
        ) : (
          <Pill>{(cell.getValue() as string) || ''}</Pill>
        ),
      Edit: () => <ModalProviderGroupSelect disabled={editDisabled} />,
    },
  ];

  return columns;
};
